import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {pct, unitPrice} from '../../domain/shouldCost';
import type {ShouldCostActions} from '../../hooks/useShouldCost';
import type {PriceModel} from '../../types/shouldCostTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RowAction} from '../components/RowAction';
import {OutcomeDialog} from './OutcomeDialog';
import {SHOULD_COST_HELP} from '../../domain/shouldCostHelp';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const DAY = new Intl.DateTimeFormat(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});

type Props = {model: PriceModel; canEdit: boolean; actions: ShouldCostActions};

/**
 * Post-negotiation actualization (spec 4.9): agreed prices against the model's prediction when each negotiation
 * closed, per pillar (line) where recorded, and the model's MAPE.
 */
export const ActualizationCard: React.FC<Props> = ({model, canEdit, actions}) => {
    const [adding, setAdding] = useState(false);
    const {outcomes, mape} = model.actualization;
    const cur = model.currency;
    return (
        <ProfileSection title="Post-negotiation actualization" help={SHOULD_COST_HELP.actualization}
                        subtitle={mape === null ? undefined : `Model MAPE ${mape}% over ${outcomes.length} negotiation${outcomes.length === 1 ? '' : 's'}`}
                        actions={canEdit && <Button size="small" startIcon={<Add/>} onClick={() => setAdding(true)}>Record outcome</Button>}>
            {outcomes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No closed negotiations yet. Record the agreed price when a negotiation closes to track how well the model predicts.
                </Typography>
            ) : (
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Negotiation outcomes">
                        <TableHead>
                            <TableRow>
                                <TableCell>Closed</TableCell>
                                <TableCell>Supplier</TableCell>
                                <TableCell align="right">Predicted</TableCell>
                                <TableCell align="right">Actual</TableCell>
                                <TableCell align="right">Δ per unit</TableCell>
                                <TableCell align="right">Variance</TableCell>
                                <TableCell>Pillars</TableCell>
                                {canEdit && <TableCell/>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {outcomes.map(o => (
                                <TableRow key={o.id}>
                                    <TableCell sx={{whiteSpace: 'nowrap'}}>{DAY.format(new Date(o.closedOn))}</TableCell>
                                    <TableCell>{o.supplier}</TableCell>
                                    <TableCell align="right">{unitPrice(o.predicted, cur)}</TableCell>
                                    <TableCell align="right">{unitPrice(o.actual, cur)}</TableCell>
                                    <TableCell align="right">{`${o.delta > 0 ? '+' : ''}${unitPrice(o.delta, cur)}`}</TableCell>
                                    <TableCell align="right">{pct(o.variancePct)}</TableCell>
                                    <TableCell>
                                        <Stack>
                                            {o.pillars.length === 0 ? '—' : o.pillars.map(p => (
                                                <Typography key={p.id} variant="caption">
                                                    {`${p.name}: ${unitPrice(p.actual, cur)} vs ${unitPrice(p.predicted, cur)}`}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    {canEdit && (
                                        <TableCell align="right">
                                            <RowAction kind="delete" label="outcome" onClick={() => void actions.removeOutcome(o.id, o.supplier)}/>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {adding && <OutcomeDialog model={model} onSubmit={input => actions.addOutcome(model, input)} onClose={() => setAdding(false)}/>}
        </ProfileSection>
    );
};
